/**
 * Autocomplete — Componente reutilizable de autocompletado.
 *
 * - freeText=true: permite texto libre, auto-fill al seleccionar (SKU/Nombre producto)
 * - freeText=false: solo permite seleccionar de la lista (Cliente, Cuenta contable)
 * - Dropdown se renderiza en un portal para evitar overflow:hidden
 */
import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'

export interface AutocompleteItem {
  id: string
  label: string
  sublabel?: string
}

interface AutocompleteProps {
  items: AutocompleteItem[]
  value: string
  onChange: (value: string) => void
  onSelect?: (item: AutocompleteItem) => void
  placeholder?: string
  disabled?: boolean
  required?: boolean
  className?: string
  freeText?: boolean
  displayValue?: string
  id?: string
  'aria-label'?: string
  'aria-labelledby'?: string
  /**
   * Avisa cada cambio del texto buscado. Junto con `serverFiltered`, permite que
   * quien usa el componente pida los resultados al servidor en vez de filtrar
   * una lista descargada entera.
   */
  onQueryChange?: (query: string) => void
  /** Si es true, `items` ya viene filtrado (por el servidor) y no se vuelve a filtrar. */
  serverFiltered?: boolean
  /** Muestra «Buscando…» mientras llegan los resultados. */
  loading?: boolean
}

export function Autocomplete({
  items,
  value,
  onChange,
  onSelect,
  placeholder = '',
  disabled = false,
  required = false,
  className = '',
  freeText = false,
  displayValue,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  onQueryChange,
  serverFiltered = false,
  loading = false,
}: AutocompleteProps) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const [dropdownPos, setDropdownPos] = useState<{
    top?: number; bottom?: number; left: number; width: number; maxHeight: number
  }>({ top: 0, left: 0, width: 0, maxHeight: 240 })

  // Compute display text: when not focused, show displayValue or resolved label
  const resolvedDisplay = useMemo(() => {
    if (displayValue !== undefined && displayValue !== null) return String(displayValue)
    if (!freeText && value) {
      const found = items.find(i => i.id === value)
      return found ? found.label : ''
    }
    return String(value || '')
  }, [displayValue, value, items, freeText])

  // The text shown in the input — always a string to avoid React warnings
  const inputText = String(open ? query : resolvedDisplay)

  // Filter items based on query
  const filtered = useMemo(() => {
    if (!open) return []
    if (serverFiltered) return items
    const q = query.toLowerCase()
    if (!q) return items
    return items.filter(item =>
      item.label.toLowerCase().includes(q) ||
      (item.sublabel?.toLowerCase().includes(q) ?? false)
    )
  }, [items, query, open, serverFiltered])

  // Position dropdown relative to input (for portal)
  const updatePosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect()
      const MAX_H = 240
      const margin = 8
      const spaceBelow = window.innerHeight - rect.bottom - margin
      const spaceAbove = rect.top - margin
      // Abrir hacia ARRIBA si abajo no cabe y arriba hay más espacio (inputs al pie de la página).
      const dropUp = spaceBelow < Math.min(MAX_H, 200) && spaceAbove > spaceBelow
      setDropdownPos({
        top: dropUp ? undefined : rect.bottom + 2,
        bottom: dropUp ? window.innerHeight - rect.top + 2 : undefined,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.max(120, Math.min(MAX_H, dropUp ? spaceAbove : spaceBelow)),
      })
    }
  }, [])

  useEffect(() => {
    if (open) {
      updatePosition()
      window.addEventListener('scroll', updatePosition, true)
      window.addEventListener('resize', updatePosition)
      return () => {
        window.removeEventListener('scroll', updatePosition, true)
        window.removeEventListener('resize', updatePosition)
      }
    }
  }, [open, updatePosition])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        // Check if click is on the dropdown portal
        const portal = document.getElementById('autocomplete-portal')
        if (portal && portal.contains(e.target as Node)) return
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightIndex] as HTMLElement
      el?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightIndex])

  const handleSelect = useCallback((item: AutocompleteItem) => {
    setOpen(false)
    setHighlightIndex(-1)
    setQuery('')
    onQueryChange?.('')
    onChange(freeText ? item.label : item.id)
    onSelect?.(item)
  }, [onChange, onSelect, freeText, onQueryChange])

  const handleFocus = () => {
    setQuery('')
    onQueryChange?.('')
    setOpen(true)
  }

  const handleInputChange = (text: string) => {
    setQuery(text)
    onQueryChange?.(text)
    setHighlightIndex(-1)
    if (!open) setOpen(true)
    if (freeText) {
      onChange(text)
    }
  }

  const handleBlur = () => {
    // Small delay to allow click on dropdown item
    setTimeout(() => {
      if (!document.getElementById('autocomplete-portal')?.contains(document.activeElement)) {
        setOpen(false)
      }
    }, 150)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setOpen(true)
      return
    }
    if (!open) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex(prev => Math.min(prev + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex(prev => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightIndex >= 0 && highlightIndex < filtered.length) {
        handleSelect(filtered[highlightIndex])
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
    } else if (e.key === 'Tab') {
      setOpen(false)
    }
  }

  // Ensure portal container exists
  useEffect(() => {
    if (!document.getElementById('autocomplete-portal')) {
      const div = document.createElement('div')
      div.id = 'autocomplete-portal'
      document.body.appendChild(div)
    }
  }, [])

  const portalEl = document.getElementById('autocomplete-portal')

  return (
    <div ref={wrapperRef} className="relative">
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={inputText}
        onChange={e => handleInputChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        title={String(resolvedDisplay || '')}
        className={`${className} text-ellipsis overflow-hidden`}
        autoComplete="off"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
      />
      {open && (filtered.length > 0 || (serverFiltered && (loading || query))) && portalEl && createPortal(
        <ul
          ref={listRef}
          style={{
            position: 'fixed',
            ...(dropdownPos.top !== undefined
              ? { top: dropdownPos.top }
              : { bottom: dropdownPos.bottom }),
            left: dropdownPos.left,
            width: Math.max(dropdownPos.width, 220),
            maxHeight: dropdownPos.maxHeight,
            zIndex: 9999,
            backgroundColor: 'rgb(var(--color-popover))',
          }}
          className="overflow-y-auto bg-popover border border-divider rounded-lg shadow-lg py-0.5"
        >
          {serverFiltered && filtered.length === 0 && (
            <li role="status" className="px-2.5 py-1.5 text-xs text-content-subtle">
              {loading ? 'Buscando…' : 'Sin resultados'}
            </li>
          )}
          {filtered.slice(0, 50).map((item, idx) => (
            <li
              key={item.id + idx}
              onMouseDown={e => { e.preventDefault(); handleSelect(item) }}
              onMouseEnter={() => setHighlightIndex(idx)}
              className={`px-2.5 py-1.5 text-xs cursor-pointer transition-colors ${
                idx === highlightIndex ? 'bg-primary-50 text-primary-hover' : 'text-content hover:bg-soft'
              }`}
            >
              <span className="font-medium">{item.label}</span>
              {item.sublabel && (
                <span className="ml-1.5 text-content-subtle">{item.sublabel}</span>
              )}
            </li>
          ))}
        </ul>,
        portalEl
      )}
    </div>
  )
}
